import { Cache, Color, FileLoader, FogExp2, LoadingManager, Scene } from 'three'
import {
  AudioController,
  type BackgroundAudioOptions,
  type VoiceAudioOptions,
} from '../audio/AudioController'
import { appConfig, AUTHORING_CAMERA_MODE } from '../config/appConfig'
import { ScreenTransition } from '../effects/ScreenTransition'
import { TimelinePlayer } from '../flow/TimelinePlayer'
import { bedroomTimeline } from '../flow/timeline'
import type {
  ActionPromptCue,
  ExteriorActionCue,
  GameplayCue,
  LightingCue,
  PhoneReactionCue,
  PhoneReplyCue,
  SceneCue,
  SceneEventCue,
  SceneStateCue,
  TimelineCue,
  TimelineState,
  TransitionCue,
} from '../flow/types'
import { ExteriorGameplayController } from '../gameplay/ExteriorGameplayController'
import { MaterialManager } from '../materials/MaterialManager'
import { VideoScreenController } from '../media/VideoScreenController'
import { BedroomScene, type SceneMetrics } from '../scene/BedroomScene'
import { CameraRig, type CameraPose } from '../scene/CameraRig'
import { ExteriorScene } from '../scene/ExteriorScene'
import { HDRIEnvironment } from '../scene/HDRIEnvironment'
import { KitchenScene } from '../scene/KitchenScene'
import { LightingRig } from '../scene/LightingRig'
import { AchievementTrail } from '../ui/AchievementTrail'
import { ActionPrompt } from '../ui/ActionPrompt'
import { AppUI } from '../ui/AppUI'
import { DialogueOverlay, type DialogueLine } from '../ui/DialogueOverlay'
import { ObjectiveOverlay } from '../ui/ObjectiveOverlay'
import { SceneEventOverlay } from '../ui/SceneEventOverlay'
import { RendererController } from './RendererController'
import { ViewportController } from './ViewportController'

type AppSceneId = 'bedroom' | 'kitchen' | 'exterior' | 'elina-interior-pending'

export class App {
  public readonly audio = new AudioController()
  public readonly video = new VideoScreenController()
  public readonly dialogue = new DialogueOverlay({
    onPhoneKeyPress: (key, kind) => {
      this.audio.playPhoneKeyTick(key, kind)
    },
  })
  public readonly sceneEvent = new SceneEventOverlay()
  public readonly screenTransition = new ScreenTransition()
  private readonly scene = new Scene()
  private readonly ui = new AppUI()
  private readonly objective = new ObjectiveOverlay()
  private readonly achievements = new AchievementTrail()
  private readonly actionPrompt = new ActionPrompt()
  private readonly canvas: HTMLCanvasElement
  private readonly loadingManager: LoadingManager
  private readonly renderer: RendererController
  private readonly cameraRig: CameraRig
  private readonly viewport: ViewportController
  private lighting: LightingRig
  private readonly materialManager: MaterialManager
  private readonly bedroom: BedroomScene
  private readonly kitchen: KitchenScene
  private readonly exterior: ExteriorScene
  private readonly exteriorEnvironment: HDRIEnvironment
  private readonly exteriorGameplay: ExteriorGameplayController
  private readonly timeline: TimelinePlayer
  private readonly dialogueQueue: DialogueLine[] = []
  private preparedVideoSource: string | null = null
  private kitchenPreparePromise: Promise<SceneMetrics> | null = null
  private exteriorPreparePromise: Promise<SceneMetrics> | null = null
  private gameStarted = false
  private activeSceneId: AppSceneId = 'bedroom'
  private pointerStart: { id: number; x: number; y: number } | null = null
  private renderLoopActive = false
  private continuousRenderLocks = 0
  private disposed = false

  public constructor(container: HTMLElement, canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.ui.setAuthoringMode(AUTHORING_CAMERA_MODE)
    this.scene.background = new Color(appConfig.scene.background)
    this.scene.fog = new FogExp2(appConfig.scene.fogColor, appConfig.scene.fogDensity)

    this.renderer = new RendererController({
      canvas,
      clearColor: appConfig.renderer.clearColor,
      exposure: appConfig.renderer.exposure,
      maxPixelRatio: appConfig.renderer.maxPixelRatio,
      onContextLost: () => this.ui.showContextLost(),
      onContextRestored: () => this.ui.showContextRestored(),
    })
    this.cameraRig = new CameraRig(canvas, appConfig)
    // Bedroom and kitchen are player-paced cinematics, so their authored
    // framing stays fixed in the player build. The exterior explicitly
    // re-enables its tightly bounded orbit after the arrival shot.
    this.cameraRig.controls.enabled = AUTHORING_CAMERA_MODE
    this.viewport = new ViewportController(
      container,
      this.cameraRig.camera,
      this.renderer,
      this.handleViewportResize,
    )
    this.lighting = new LightingRig(this.scene, appConfig.scene, appConfig.lighting)

    this.loadingManager = this.createLoadingManager()
    this.materialManager = new MaterialManager(
      this.loadingManager,
      appConfig.assets.animeMatcaps,
      this.renderer.getMaxAnisotropy(),
    )
    this.bedroom = new BedroomScene(
      this.scene,
      this.loadingManager,
      this.materialManager,
      this.video,
    )
    this.kitchen = new KitchenScene(this.scene, this.loadingManager, this.materialManager)
    this.exterior = new ExteriorScene(this.scene, this.loadingManager, this.materialManager)
    this.exteriorEnvironment = new HDRIEnvironment(this.loadingManager, this.renderer.renderer)
    this.exteriorGameplay = new ExteriorGameplayController({
      canvas,
      camera: this.cameraRig.camera,
      controls: this.cameraRig.controls,
      scene: this.exterior,
      config: appConfig.exterior.gameplay,
      onRender: () => this.requestRender(),
      onWoosh: () => {
        this.audio.playWoosh()
      },
      onFeedback: (text, clientX, clientY, tone) => {
        this.objective.showFeedback(text, clientX, clientY, tone)
      },
      onHint: (text) => {
        this.objective.updateProgress(text)
      },
      onKeyCollected: () => {
        this.achievements.unlock({
          id: 'elinas-spare-key',
          description: 'Found the Spare Key.',
          icon: 'key',
        })
      },
      onComplete: (gameplayId) => {
        if (!this.completeGameplay(gameplayId)) {
          console.warn(`Exterior gameplay gate "${gameplayId}" was no longer active.`)
        }
      },
    })
    this.timeline = new TimelinePlayer(bedroomTimeline, {
      onCueStart: this.handleTimelineCueStart,
      onComplete: this.handleTimelineComplete,
      unlockAudio: this.unlockTimelineAudio,
      playBackgroundAudio: this.playTimelineBackgroundAudio,
      stopBackgroundAudio: (fadeOut) => this.audio.stop(fadeOut),
      playVoiceAudio: this.playTimelineVoiceAudio,
      stopVoiceAudio: (fadeOut) => this.audio.stopVoice(fadeOut),
      playVideo: this.playTimelineVideo,
      powerDownVideo: this.powerDownTimelineVideo,
      playCameraIntro: async () => this.playCameraIntro(),
      playKitchenCameraTour: async () => this.playKitchenCameraTour(),
      playKitchenCameraTourStep: async (stepIndex) => this.playKitchenCameraTourStep(stepIndex),
      fadeLighting: this.fadeTimelineLighting,
      swapScene: this.swapTimelineScene,
      applySceneState: this.applyTimelineSceneState,
      showDialogue: (line) => this.showDialogue(line),
      showPhoneReaction: this.showTimelinePhoneReaction,
      playPhoneReply: this.playTimelinePhoneReply,
      hideDialogue: () => this.dialogue.hide(),
      playSceneEvent: this.playTimelineSceneEvent,
      playTransition: this.playTimelineTransition,
      playExteriorAction: this.playTimelineExteriorAction,
      showActionPrompt: this.showTimelineActionPrompt,
      beginGameplay: this.beginTimelineGameplay,
      endGameplay: this.endTimelineGameplay,
    })

    window.addEventListener('keydown', this.handleKeyDown)
    window.addEventListener('pointerdown', this.handlePointerDown)
    window.addEventListener('pointermove', this.handlePointerMove, { passive: true })
    window.addEventListener('pointerup', this.handlePointerUp)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.cameraRig.controls.addEventListener('change', this.requestRender)
  }

  private createLoadingManager(): LoadingManager {
    const manager = new LoadingManager()
    const showInitialLoading = (url: string, loaded: number, total: number): void => {
      if (!this.gameStarted && !this.disposed) this.ui.setLoading(loaded, total, url)
    }
    manager.onStart = showInitialLoading
    manager.onProgress = showInitialLoading
    manager.onError = (url) => console.error(`Failed to load scene asset: ${url}`)
    return manager
  }

  public async start(): Promise<SceneMetrics> {
    try {
      // Download every 3D scene needed by this chapter before START. Only the
      // raw deferred files are cached here; kitchen and exterior decoding still
      // happens later so mobile memory does not hold three parsed scenes at once.
      Cache.enabled = true
      const [metrics] = await Promise.all([
        this.bedroom.load(appConfig.assets.bedroom),
        this.preloadDeferredSceneAssets(),
        this.prepareTimelineMedia(),
      ])
      this.releaseInitialAssetCache()
      if (this.disposed) {
        this.releaseLateInitialLoad()
        return metrics
      }
      // Upload the visible bedroom while the opaque START overlay still hides
      // the canvas, then stop drawing until the player actually begins.
      await this.waitForRenderFrames(2)
      await this.ui.waitForStart(metrics)
      this.beginExperience()
      return metrics
    } catch (error) {
      if (!this.disposed) this.ui.showError(error)
      throw error
    }
  }

  private beginExperience(): void {
    if (this.disposed) return
    this.gameStarted = true
    this.ui.hideStartOverlay()
    void this.timeline.run().catch((error) => {
      if (this.disposed) return
      this.ui.showError(error)
      console.error('Story timeline failed.', error)
    })
  }

  private async prepareTimelineMedia(): Promise<void> {
    try {
      await this.audio.preparePhoneKeyTicks(appConfig.assets.phoneKeyTicks)
    } catch (error) {
      console.warn('Phone keyboard sound could not be prepared before START.', error)
    }

    try {
      await this.audio.prepareWoosh(appConfig.assets.woosh)
    } catch (error) {
      console.warn('Woosh interaction sound could not be prepared before START.', error)
    }

    let videoSource: string | null = null
    for (const cue of bedroomTimeline.cues) {
      if (cue.type === 'video' && cue.action === 'play') {
        videoSource = cue.source
        break
      }
    }
    if (!videoSource) return

    try {
      await this.video.prepareSource(videoSource)
      this.preparedVideoSource = videoSource
    } catch (error) {
      this.preparedVideoSource = null
      console.warn('Timeline video could not be prepared before START.', error)
    }
  }

  private async preloadDeferredSceneAssets(): Promise<void> {
    const loader = new FileLoader(this.loadingManager).setResponseType('arraybuffer')
    await Promise.all([
      loader.loadAsync(appConfig.assets.kitchen),
      loader.loadAsync(appConfig.assets.exterior),
      loader.loadAsync(appConfig.assets.exteriorEnvironment),
    ])
  }

  private releaseInitialAssetCache(): void {
    Cache.remove(appConfig.assets.bedroom)
    for (const url of Object.values(appConfig.assets.animeMatcaps)) Cache.remove(url)
  }

  private releaseDeferredAssetCache(): void {
    Cache.remove(appConfig.assets.kitchen)
    Cache.remove(appConfig.assets.exterior)
    Cache.remove(appConfig.assets.exteriorEnvironment)
  }

  private readonly renderFrame = (time: number): void => {
    if (this.disposed || document.hidden) {
      this.stopRenderLoop()
      return
    }

    const cameraChanged = this.cameraRig.update()
    this.screenTransition.update(time)
    this.renderer.render(this.scene, this.cameraRig.camera)
    this.screenTransition.render(this.renderer.renderer)

    if (
      this.continuousRenderLocks === 0
      && !this.video.isPlaying
      && this.screenTransition.state !== 'covering'
      && this.screenTransition.state !== 'revealing'
      && !cameraChanged
    ) this.stopRenderLoop()
  }

  private readonly requestRender = (): void => {
    if (this.disposed || document.hidden || this.renderLoopActive) return
    this.renderLoopActive = true
    this.renderer.setAnimationLoop(this.renderFrame)
  }

  private readonly handleViewportResize = (): void => {
    this.applyResponsiveCameraFraming()
    this.requestRender()
  }

  private applyResponsiveCameraFraming(): void {
    const isMobile = window.matchMedia('(max-width: 920px)').matches
    const fov = !isMobile
      ? appConfig.camera.fov
      : this.activeSceneId === 'kitchen'
        ? appConfig.kitchen.mobileFov
        : appConfig.camera.mobileFov
    this.cameraRig.setFov(fov)
  }

  private stopRenderLoop(): void {
    if (!this.renderLoopActive) return
    this.renderLoopActive = false
    this.renderer.setAnimationLoop(null)
  }

  private acquireContinuousRender(): () => void {
    this.continuousRenderLocks += 1
    this.requestRender()
    let released = false
    return () => {
      if (released) return
      released = true
      this.continuousRenderLocks = Math.max(0, this.continuousRenderLocks - 1)
      this.requestRender()
    }
  }

  private async renderContinuously<T>(task: () => Promise<T>): Promise<T> {
    const release = this.acquireContinuousRender()
    try {
      return await task()
    } finally {
      release()
    }
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.hidden) this.stopRenderLoop()
    else this.requestRender()
  }

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    const target = event.target
    const isTyping =
      target instanceof HTMLInputElement
      || target instanceof HTMLTextAreaElement
      || (target instanceof HTMLElement && target.isContentEditable)

    if (isTyping || target instanceof HTMLButtonElement || event.repeat) return
    if (event.code === 'Escape' && this.dialogue.isVisible) {
      event.preventDefault()
      if (!this.advanceStory()) this.hideDialogue()
      return
    }
    if (event.code === 'Space' && this.gameStarted) {
      event.preventDefault()
      this.advanceStory()
      return
    }
    if (!AUTHORING_CAMERA_MODE || !event.shiftKey || event.code !== 'KeyC') return
    event.preventDefault()
    void this.copyCameraPose()
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return
    this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY }
  }

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (this.activeSceneId !== 'exterior') return
    this.exteriorGameplay.handlePointerMove(event.clientX, event.clientY)
  }

  private readonly handlePointerUp = (event: PointerEvent): void => {
    const start = this.pointerStart
    this.pointerStart = null
    if (!start || start.id !== event.pointerId || event.button !== 0) return
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) return
    const target = event.target
    if (
      target instanceof Element
      && target.closest('button, a, input, textarea, select, [data-story-scroll-region]')
    ) return
    if (
      this.activeSceneId === 'exterior'
      && this.exteriorGameplay.handleClick(event.clientX, event.clientY)
    ) return
    if (this.gameStarted) this.advanceStory()
  }

  private async writeClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (error) {
      console.warn('Could not copy the camera pose to the clipboard.', error)
      return false
    }
  }

  public async copyCameraPose(): Promise<CameraPose> {
    const pose = this.getCameraPose()
    const serializedPose = JSON.stringify(pose, null, 2)
    console.info(`[Camera pose]\n${serializedPose}`)
    const copied = await this.writeClipboard(serializedPose)
    this.ui.showCameraPoseCaptured(copied)
    return pose
  }

  public getCameraPose(): CameraPose {
    return this.cameraRig.getPose()
  }

  public resetCamera(): void {
    this.cameraRig.reset()
    this.requestRender()
  }

  public playCameraIntro(): Promise<void> {
    return this.renderContinuously(() => this.cameraRig.playIntro())
  }

  public playKitchenCameraTour(): Promise<void> {
    return this.renderContinuously(() => (
      this.cameraRig.playSequence(appConfig.kitchen.cameraTour.steps)
    ))
  }

  public playKitchenCameraTourStep(stepIndex: number): Promise<void> {
    const step = appConfig.kitchen.cameraTour.steps[stepIndex]
    if (!step) {
      throw new RangeError(`Kitchen camera tour step ${stepIndex} does not exist.`)
    }
    return this.renderContinuously(() => this.cameraRig.playSequence([step]))
  }

  public setGroupVisible(group: string, visible: boolean): number {
    if (this.activeSceneId === 'elina-interior-pending') return 0
    const matches = this.activeSceneId === 'exterior'
      ? this.exterior.setGroupVisible(group, visible)
      : this.activeSceneId === 'kitchen'
        ? this.kitchen.setGroupVisible(group, visible)
        : this.bedroom.setGroupVisible(group, visible)
    if (matches > 0) this.requestRender()
    return matches
  }

  public listGroups(): string[] {
    if (this.activeSceneId === 'elina-interior-pending') return []
    return this.activeSceneId === 'exterior'
      ? this.exterior.listGroups()
      : this.activeSceneId === 'kitchen'
        ? this.kitchen.listGroups()
        : this.bedroom.listGroups()
  }

  public showDialogue(line: DialogueLine): void {
    this.dialogue.show(line)
    const delivery = line.source === 'phone' ? 'phone-text' : line.source
    if (delivery === 'phone-text') {
      this.requestRender()
      void this.screenTransition.reveal().finally(this.requestRender)
    }
    this.ui.setGamePhase('dialogue', `${line.speaker ?? 'Narration'} · click or Space to continue`)
  }

  public hideDialogue(): void {
    this.dialogue.hide()
    if (this.gameStarted) this.ui.setGamePhase('explore', 'Click or Space to continue')
  }

  public queueDialogue(...lines: DialogueLine[]): void {
    this.dialogueQueue.push(...lines)
    if (this.gameStarted && !this.timeline.isRunning && !this.dialogue.isVisible) {
      this.advanceStory()
    }
  }

  public advanceStory(): boolean {
    if (!this.gameStarted) return false
    if (this.timeline.advance()) return true
    if (this.timeline.isRunning) return false
    const next = this.dialogueQueue.shift()
    if (next) {
      this.showDialogue(next)
      return true
    }
    if (this.dialogue.isVisible) {
      this.hideDialogue()
      return true
    }
    return false
  }

  public async playVideo(url: string): Promise<HTMLVideoElement> {
    this.requestRender()
    const video = await this.video.setSource(url)
    this.requestRender()
    return video
  }

  public playBackgroundAudio(
    url: string,
    options?: BackgroundAudioOptions,
  ): Promise<AudioBufferSourceNode> {
    return this.audio.playBackground(url, options)
  }

  public stopBackgroundAudio(fadeOut?: number): void {
    this.audio.stop(fadeOut)
  }

  public playVoiceAudio(
    url: string,
    options?: VoiceAudioOptions,
  ): Promise<AudioBufferSourceNode> {
    return this.audio.playVoice(url, options)
  }

  public stopVoiceAudio(fadeOut?: number): void {
    this.audio.stopVoice(fadeOut)
  }

  public completeGameplay(gameplayId: string): boolean {
    return this.timeline.completeGameplay(gameplayId)
  }

  public getTimelineState(): TimelineState {
    return this.timeline.state
  }

  public get hasStarted(): boolean {
    return this.gameStarted
  }

  public get isDisposed(): boolean {
    return this.disposed
  }

  public get currentSceneId(): AppSceneId {
    return this.activeSceneId
  }

  public dispose(): void {
    if (this.disposed) return
    this.disposed = true
    window.removeEventListener('keydown', this.handleKeyDown)
    window.removeEventListener('pointerdown', this.handlePointerDown)
    window.removeEventListener('pointermove', this.handlePointerMove)
    window.removeEventListener('pointerup', this.handlePointerUp)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.cameraRig.controls.removeEventListener('change', this.requestRender)
    this.stopRenderLoop()
    this.timeline.abort()
    this.audio.dispose()
    this.video.dispose()
    this.dialogue.dispose()
    this.sceneEvent.dispose()
    this.objective.dispose()
    this.achievements.dispose()
    this.actionPrompt.dispose()
    this.screenTransition.dispose()
    this.bedroom.dispose()
    this.kitchen.dispose()
    this.exteriorGameplay.dispose()
    this.exterior.dispose()
    this.exteriorEnvironment.dispose(this.scene)
    this.materialManager.dispose()
    this.releaseInitialAssetCache()
    this.releaseDeferredAssetCache()
    this.lighting.dispose(this.scene)
    this.viewport.dispose()
    this.cameraRig.dispose()
    this.renderer.dispose()
    this.ui.dispose()
    this.scene.clear()
  }

  private readonly handleTimelineCueStart = (cue: TimelineCue): void => {
    if (cue.phase && cue.status) this.ui.setGamePhase(cue.phase, cue.status)
  }

  private readonly handleTimelineComplete = (): void => {
    this.dialogue.hide()
    const status = this.activeSceneId === 'elina-interior-pending'
      ? 'Interior handoff ready'
      : this.activeSceneId === 'exterior'
      ? AUTHORING_CAMERA_MODE
        ? 'Exterior ready · explore freely and use Shift+C to capture a camera pose'
        : 'Exterior ready'
      : this.activeSceneId === 'kitchen'
        ? 'Kitchen table ready'
        : 'Scene timeline complete'
    this.ui.setGamePhase(
      'explore',
      status,
    )
    if (this.dialogueQueue.length > 0) this.advanceStory()
    this.requestRender()
  }

  private readonly unlockTimelineAudio = async (): Promise<void> => {
    try {
      await this.audio.unlock()
    } catch (error) {
      console.warn('Web Audio could not be unlocked.', error)
    }
  }

  private readonly playTimelineBackgroundAudio = async (
    source: string,
    options?: BackgroundAudioOptions,
  ): Promise<void> => {
    try {
      await this.audio.playBackground(source, options)
    } catch (error) {
      console.warn('Timeline background audio could not start.', error)
    }
  }

  private readonly playTimelineVoiceAudio = async (
    source: string,
    options?: VoiceAudioOptions,
  ): Promise<void> => {
    await this.audio.playVoice(source, options)
  }

  private readonly playTimelineVideo = async (source: string): Promise<void> => {
    this.requestRender()
    try {
      if (this.preparedVideoSource === source) await this.video.play()
      else await this.video.setSource(source)
    } catch (error) {
      console.warn('Timeline TV video could not start.', error)
    } finally {
      this.requestRender()
    }
  }

  private readonly powerDownTimelineVideo = async (): Promise<void> => {
    await this.renderContinuously(() => this.video.powerDown())
  }

  private readonly playTimelineSceneEvent = async (cue: SceneEventCue): Promise<void> => {
    if (cue.sound === 'phone-beep') {
      void this.audio.playNotificationBeep().catch((error) => {
        console.warn('Timeline notification sound could not play.', error)
      })
    }
    await this.sceneEvent.play(cue)
  }

  private readonly playTimelineTransition = async (cue: TransitionCue): Promise<void> => {
    if (cue.effect === 'phone-focus') {
      await this.renderContinuously(() => (
        this.screenTransition.playCover(cue.durationMs, cue.revealDurationMs, cue.effect)
      ))
      return
    }

    if (cue.destination !== 'elina-interior' || this.activeSceneId !== 'exterior') {
      throw new Error(`Interior transition cue "${cue.id}" requires the active exterior scene.`)
    }

    this.ui.setSceneUiHidden(true)
    this.cameraRig.controls.enabled = false
    await this.renderContinuously(async () => {
      await this.screenTransition.playCover(cue.durationMs, 900, cue.effect)

      // There is no interior GLB yet. Release the exterior under the fully
      // opaque dots and hold black as the exact future scene-install seam.
      this.exteriorGameplay.dispose()
      this.exterior.dispose()
      this.exteriorEnvironment.dispose(this.scene)
      this.lighting.dispose(this.scene)
      this.renderer.releaseSceneReferences()
      this.scene.background = new Color(0x000000)
      this.scene.environment = null
      this.scene.fog = null
      this.activeSceneId = 'elina-interior-pending'
      await this.waitForRenderFrames(1)
    })
  }

  private readonly playTimelineExteriorAction = async (
    cue: ExteriorActionCue,
  ): Promise<void> => {
    if (cue.action !== 'collect-door-key' || this.activeSceneId !== 'exterior') {
      throw new Error(`Exterior action cue "${cue.id}" requires the active exterior scene.`)
    }
    await this.renderContinuously(() => this.exteriorGameplay.collectRevealedKey())
  }

  private readonly showTimelineActionPrompt = (cue: ActionPromptCue): Promise<void> => {
    if (cue.action !== 'enter-elinas-house') {
      throw new Error(`Unknown story action prompt "${cue.action}".`)
    }
    return this.actionPrompt.show(cue.label)
  }

  private readonly applyTimelineSceneState = async (cue: SceneStateCue): Promise<void> => {
    if (cue.action !== 'set-kitchen-packed' || this.activeSceneId !== 'kitchen') {
      throw new Error(`Scene-state cue "${cue.id}" requires the active kitchen scene.`)
    }

    await this.renderContinuously(async () => {
      await this.screenTransition.playCover(
        cue.durationMs,
        cue.revealDurationMs,
        cue.effect,
      )

      const packedState = appConfig.kitchen.tableStates.packed
      for (const group of [...packedState.hideGroups, ...packedState.hideNodes]) {
        this.kitchen.setGroupVisible(group, false)
      }
      for (const group of packedState.showGroups) {
        const matches = this.kitchen.setGroupVisible(group, true)
        if (matches === 0) console.warn(`Packed kitchen group "${group}" was not found.`)
      }
      const transformed = this.kitchen.applyAuthoredTransforms('packed')
      if (transformed === 0) {
        console.warn('No packed-state transforms were found in the kitchen GLB.')
      }

      // This terminal state never returns to the open-box props. Detach them
      // under black and free only geometry/material/texture resources that are
      // not shared by the retained kitchen or packed objects.
      this.kitchen.releaseGroups(packedState.hideGroups, packedState.hideNodes)
      this.renderer.releaseSceneReferences()

      // Install the packed shot's opening pose while the dots are fully opaque.
      // The camera then moves to its final authored pose as the new state appears.
      this.cameraRig.setPose(appConfig.kitchen.packedCamera.start, false)
      await this.waitForRenderFrames(2)
      await Promise.all([
        this.screenTransition.revealDots(cue.revealDurationMs),
        this.cameraRig.playSequence([appConfig.kitchen.packedCamera.move]),
      ])
    })

  }

  private readonly fadeTimelineLighting = async (cue: LightingCue): Promise<void> => {
    await Promise.all([
      this.activeSceneId === 'bedroom' ? this.ensureKitchenPrepared() : Promise.resolve(),
      this.renderContinuously(async () => {
        await Promise.all([
          this.lighting.fadeTo(cue.intensityScale, cue.durationMs),
          this.renderer.fadeExposureTo(cue.intensityScale, cue.durationMs),
        ])
      }),
    ])
  }

  private readonly swapTimelineScene = async (cue: SceneCue): Promise<void> => {
    if (cue.sceneId === this.activeSceneId) return

    if (cue.sceneId === 'exterior') {
      if (this.activeSceneId !== 'kitchen') {
        throw new Error(`Exterior scene cue "${cue.id}" requires the active kitchen scene.`)
      }
      await this.swapKitchenForExterior(cue)
      return
    }

    if (this.activeSceneId !== 'bedroom') {
      throw new Error(`Kitchen scene cue "${cue.id}" requires the active bedroom scene.`)
    }

    await this.renderContinuously(async () => {
      await this.ensureKitchenPrepared()

      // The old room is already almost dark. An opaque dot cover gives us one
      // safe frame to dispose it and install the prepared kitchen out of sight.
      this.screenTransition.coverInstant(cue.revealEffect, cue.revealDurationMs)
      this.video.dispose()
      this.bedroom.dispose()
      this.lighting.dispose(this.scene)
      this.renderer.releaseSceneReferences()

      this.scene.background = new Color(appConfig.kitchen.scene.background)
      this.scene.fog = new FogExp2(
        appConfig.kitchen.scene.fogColor,
        appConfig.kitchen.scene.fogDensity,
      )
      this.lighting = new LightingRig(
        this.scene,
        appConfig.kitchen.scene,
        appConfig.kitchen.lighting,
      )
      this.kitchen.show()
      this.cameraRig.setPose(appConfig.kitchen.camera)
      this.activeSceneId = 'kitchen'
      this.applyResponsiveCameraFraming()
      await this.renderer.fadeExposureTo(1, 0)

      // Let the hidden kitchen compile/upload during black rather than on the
      // first visible reveal frame.
      await this.waitForRenderFrames(2)
      await this.screenTransition.revealDots(cue.revealDurationMs)

      // The exterior bytes are already present from the initial loading
      // screen. Decode and prepare them now, at the beginning of the kitchen
      // chapter, instead of waiting until its final two dialogue cards.
      void this.ensureExteriorPrepared().catch((error: unknown) => {
        console.warn('The exterior could not be prepared in the background.', error)
      })
    })
  }

  private async swapKitchenForExterior(cue: SceneCue): Promise<void> {
    await this.renderContinuously(async () => {
      await this.ensureExteriorPrepared()

      // Unlike the already-dark bedroom swap, this transition visibly grows
      // the dot field across the packed kitchen before replacing the scene.
      await this.screenTransition.playCover(
        cue.coverDurationMs ?? 1000,
        cue.revealDurationMs,
        cue.revealEffect,
      )

      this.kitchen.dispose()
      this.lighting.dispose(this.scene)
      this.renderer.releaseSceneReferences()

      this.scene.background = new Color(appConfig.exterior.scene.background)
      this.scene.fog = new FogExp2(
        appConfig.exterior.scene.fogColor,
        appConfig.exterior.scene.fogDensity,
      )
      this.lighting = new LightingRig(
        this.scene,
        appConfig.exterior.scene,
        appConfig.exterior.lighting,
      )
      this.exterior.show()
      if (!this.exteriorEnvironment.apply(this.scene, appConfig.exterior.environment)) {
        console.warn('The exterior HDR environment was unavailable; using the fallback sky color.')
      }

      // Keep input disabled through the reveal, readable wide hold, and GSAP
      // arrival so a wheel/drag cannot alter either authored endpoint. Remove
      // the previous scene's player limits first: OrbitControls.update() still
      // clamps programmatic poses while controls are disabled, and the shared
      // maxDistance would otherwise pull this wide exterior pose toward the
      // house before the first revealed frame.
      this.cameraRig.controls.enabled = false
      this.cameraRig.setControlLimits(appConfig.exterior.controls.authoring)
      this.cameraRig.setPose(appConfig.exterior.camera)
      this.activeSceneId = 'exterior'
      this.applyResponsiveCameraFraming()
      await this.renderer.fadeExposureTo(1, 0)

      await this.waitForRenderFrames(2)
      // Reveal the authored full-house pose before moving. Running these in
      // parallel hid the first part of the camera path behind the black dots,
      // making the visible shot appear to begin halfway through the move.
      await this.screenTransition.revealDots(cue.revealDurationMs)
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, appConfig.exterior.cameraArrivalHoldMs)
      })
      await this.cameraRig.playSequence([appConfig.exterior.cameraArrival])

      // Bounds are installed only after the wide arrival move so they cannot
      // clamp its authored poses. Authoring mode stays fully movable; player
      // mode uses the approved orbit edges and a fixed zoom radius.
      this.cameraRig.setControlLimits(
        AUTHORING_CAMERA_MODE
          ? appConfig.exterior.controls.authoring
          : appConfig.exterior.controls.player,
      )
      this.cameraRig.controls.enabled = true
      this.cameraRig.controls.saveState()
    })
  }

  private ensureKitchenPrepared(): Promise<SceneMetrics> {
    if (!this.kitchenPreparePromise) {
      this.kitchenPreparePromise = this.kitchen.prepare(appConfig.assets.kitchen)
        .then((metrics) => {
          // A page can close while the deferred GLB request is in flight. The
          // loader itself cannot be cancelled, so release the late result as
          // soon as it arrives instead of leaving it resident off-scene.
          if (this.disposed) {
            this.kitchen.dispose()
            this.materialManager.dispose()
          }
          return metrics
        })
        .catch((error: unknown) => {
          this.kitchenPreparePromise = null
          if (this.disposed) {
            this.kitchen.dispose()
            this.materialManager.dispose()
          }
          throw error
        })
        .finally(() => Cache.remove(appConfig.assets.kitchen))
    }
    return this.kitchenPreparePromise
  }

  private ensureExteriorPrepared(): Promise<SceneMetrics> {
    if (!this.exteriorPreparePromise) {
      const environmentPromise = this.exteriorEnvironment
        .prepare(appConfig.assets.exteriorEnvironment)
        .catch((error: unknown) => {
          console.warn('The exterior HDR environment could not be prepared.', error)
          return null
        })

      this.exteriorPreparePromise = Promise.all([
        this.exterior.prepare(appConfig.assets.exterior),
        environmentPromise,
      ])
        .then(([metrics]) => {
          if (this.disposed) {
            this.exterior.dispose()
            this.exteriorEnvironment.dispose(this.scene)
            this.materialManager.dispose()
          }
          return metrics
        })
        .catch((error: unknown) => {
          this.exteriorPreparePromise = null
          if (this.disposed) {
            this.exterior.dispose()
            this.exteriorEnvironment.dispose(this.scene)
            this.materialManager.dispose()
          }
          throw error
        })
        .finally(() => {
          Cache.remove(appConfig.assets.exterior)
          Cache.remove(appConfig.assets.exteriorEnvironment)
        })
    }
    return this.exteriorPreparePromise
  }

  private releaseLateInitialLoad(): void {
    this.video.dispose()
    this.bedroom.dispose()
    this.materialManager.dispose()
    this.releaseInitialAssetCache()
    this.releaseDeferredAssetCache()
  }

  private waitForRenderFrames(count: number): Promise<void> {
    const release = this.acquireContinuousRender()
    return new Promise((resolve) => {
      let remaining = Math.max(1, count)
      const next = (): void => {
        remaining -= 1
        if (remaining <= 0) {
          release()
          resolve()
          return
        }
        window.requestAnimationFrame(next)
      }
      window.requestAnimationFrame(next)
    })
  }

  private readonly showTimelinePhoneReaction = (cue: PhoneReactionCue): void => {
    this.dialogue.showPhoneReaction(cue)
    this.ui.setGamePhase('dialogue', `${cue.line.speaker ?? 'Narration'} · click or Space to continue`)
  }

  private readonly playTimelinePhoneReply = async (cue: PhoneReplyCue): Promise<void> => {
    this.ui.setGamePhase(cue.phase ?? 'phone-reply', cue.status ?? 'Replying to Elina')
    const deliveryFailed = await this.dialogue.playPhoneReply(cue)
    if (!deliveryFailed) return
    if (cue.errorSound === 'delivery-failed') {
      void this.audio.playDeliveryError().catch((error) => {
        console.warn('Timeline delivery-error sound could not play.', error)
      })
    }
    this.ui.setGamePhase('dialogue', 'Message delivery failed · click or Space to continue')
  }

  private readonly beginTimelineGameplay = (cue: GameplayCue): void => {
    const message = cue.instructions ?? cue.status ?? cue.title
    this.ui.setGamePhase(cue.phase ?? 'gameplay', message)
    this.objective.showObjective(cue.title, cue.instructions)

    const activateInteraction = (): void => {
      if (this.activeSceneId !== 'exterior') {
        console.warn(`Gameplay cue "${cue.gameplayId}" requires the exterior scene.`)
        return
      }
      if (!this.exteriorGameplay.begin(cue.gameplayId, {
        missText: cue.missFeedback,
        hint: cue.hint,
      })) {
        console.warn(`Gameplay cue "${cue.gameplayId}" has no exterior interaction handler.`)
        return
      }
      this.canvas.focus({ preventScroll: true })
    }

    if (cue.tutorial) {
      this.objective.showTutorial(cue.tutorial, activateInteraction)
    } else {
      activateInteraction()
    }
  }

  private readonly endTimelineGameplay = (cue: GameplayCue): void => {
    this.exteriorGameplay.end(cue.gameplayId)
    if (cue.completesObjective) {
      this.objective.complete(cue.title)
    } else if (cue.progressText) {
      this.objective.updateProgress(cue.progressText)
    }
  }
}
